import EngagementIcon from '@assets/icons/EngagementIcon'
import { colors } from '@globals/themes/defaultTheme'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { PointsEstimateInfoBox } from '@shared/PointsEstimateInfoBox'
import MultiplicationT from '@shared/translations/MultiplicationT'
import { T } from '@transifex/react'
import { ReactNode } from 'react'

type PointsEstimateInfoDisplayProps = {
  amount: number
  recipientCount: number
  allocationPoints: number
  totalSpend: number
  showEstimate: boolean
  remainingMessage?: ReactNode
  afterSendingMessage?: ReactNode
}

const PointsEstimateInfoDisplay: React.FC<PointsEstimateInfoDisplayProps> = ({
  amount,
  recipientCount,
  allocationPoints,
  totalSpend,
  showEstimate,
  remainingMessage,
  afterSendingMessage,
}) => {
  const defaultRemainingMessage = (
    <T
      _str="You have {remaining} left to give"
      remaining={
        <b>
          <T
            _str="{remainingNum, plural, one {{remaining} point} other {{remaining} points}}"
            remainingNum={allocationPoints}
            remaining={allocationPoints.toLocaleString()}
          />
        </b>
      }
    />
  )

  const defaultAfterSendingMessage = (
    <T
      _str="After sending this message, you will have {points} left to give"
      points={
        <b>
          <T
            _str="{remainingNum, plural, one {{remaining} point} other {{remaining} points}}"
            remainingNum={allocationPoints - totalSpend}
            remaining={(allocationPoints - totalSpend).toLocaleString()}
          />
        </b>
      }
    />
  )
  if (!showEstimate) {
    return (
      <PointsEstimateInfoBox>
        <Stack direction="row" useFlexGap gap={1}>
          <EngagementIcon fill={colors.bluePrimary} />
          <Typography>{remainingMessage ?? defaultRemainingMessage}</Typography>
        </Stack>
      </PointsEstimateInfoBox>
    )
  }

  return (
    <PointsEstimateInfoBox>
      <Stack direction="row" useFlexGap gap={1}>
        <EngagementIcon fill={colors.bluePrimary} />
        <Stack direction="column">
          <Typography>
            <MultiplicationT
              factor1={
                <T
                  _str="{pointsNum, plural, one {{points} point} other {{points} points}}"
                  pointsNum={amount}
                  points={amount.toLocaleString()}
                />
              }
              factor2={
                <T
                  _str="{totalRecipientsNum, plural, one {{totalRecipients} recipient} other {{totalRecipients} recipients}}"
                  totalRecipientsNum={recipientCount}
                  totalRecipients={recipientCount.toLocaleString()}
                />
              }
              result={
                <T
                  _str="{totalSpendNum, plural, one {{totalSpend} point} other {{totalSpend} points}}"
                  totalSpendNum={totalSpend}
                  totalSpend={totalSpend.toLocaleString()}
                />
              }
            />
          </Typography>
          <Typography>{afterSendingMessage ?? defaultAfterSendingMessage}</Typography>
        </Stack>
      </Stack>
    </PointsEstimateInfoBox>
  )
}

export default PointsEstimateInfoDisplay
